import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Users, DollarSign, BarChart3, TrendingUp, Plus, 
  Calendar, Activity, Download, RefreshCw, Filter,
  CheckCircle, XCircle, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { format, subDays, parseISO } from 'date-fns';
import * as XLSX from 'xlsx';

const Dashboard = () => {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [registrations, setRegistrations] = useState([]);
  const [registrationTrend, setRegistrationTrend] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Helper function to extract registrant name from form_data
  const getRegistrantName = (formData) => {
    if (!formData || typeof formData !== 'object') return 'Guest';
    
    // Check for direct name field
    if (formData.name) return formData.name;
    if (formData.full_name) return formData.full_name;
    
    // Look for name type field (has _first, _last suffixes)
    const keys = Object.keys(formData);
    const firstNameKey = keys.find(k => k.endsWith('_first'));
    const lastNameKey = keys.find(k => k.endsWith('_last'));
    
    if (firstNameKey || lastNameKey) {
      const firstName = formData[firstNameKey] || '';
      const lastName = formData[lastNameKey] || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    
    // Look for a field value that's an object with first_name/last_name
    for (const key of keys) {
      const value = formData[key];
      if (value && typeof value === 'object' && (value.first_name || value.full_name)) {
        if (value.full_name) return value.full_name;
        const fn = value.first_name || '';
        const ln = value.last_name || '';
        return `${fn} ${ln}`.trim() || 'Guest';
      }
    }
    
    // Fallback to email
    const emailKey = keys.find(k => formData[k] && String(formData[k]).includes('@'));
    if (emailKey) return formData[emailKey];
    
    return 'Guest';
  };

  // Computed stats based on selected campaign
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    inactiveCampaigns: 0,
    totalRegistrations: 0,
    paidRegistrations: 0,
    pendingRegistrations: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (campaigns.length > 0) {
      computeStats();
      generateTrendData();
    }
  }, [selectedCampaign, campaigns, registrations]);

  const fetchDashboardData = async () => {
    try {
      const campaignsRes = await api.get('/campaigns');
      setCampaigns(campaignsRes.data);

      // Fetch registrations for all campaigns user has access to
      const allRegistrations = [];
      for (const campaign of campaignsRes.data) {
        try {
          const regRes = await api.get(`/campaigns/${campaign.id}/registrations`);
          allRegistrations.push(...regRes.data.map(r => ({
            ...r,
            campaignId: campaign.id,
            campaignName: campaign.name
          })));
        } catch (e) {
          // Skip if no access or no registrations
        }
      }
      setRegistrations(allRegistrations);

    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const computeStats = () => {
    // Filter campaigns based on selection
    const filteredCampaigns = selectedCampaign === 'all' 
      ? campaigns 
      : campaigns.filter(c => c.id === selectedCampaign);

    // Filter registrations based on selection
    const filteredRegistrations = selectedCampaign === 'all'
      ? registrations
      : registrations.filter(r => r.campaignId === selectedCampaign);

    // Calculate stats
    const activeCampaigns = filteredCampaigns.filter(c => c.status === 'active').length;
    const closedCampaigns = filteredCampaigns.filter(c => c.status === 'closed' || c.status === 'archived').length;
    const draftCampaigns = filteredCampaigns.filter(c => c.status === 'draft').length;
    const paidRegs = filteredRegistrations.filter(r => r.payment_status === 'completed');
    const pendingRegs = filteredRegistrations.filter(r => r.payment_status === 'pending');
    const totalRevenue = paidRegs.reduce((sum, r) => sum + (r.total_amount || 0), 0);

    setStats({
      totalCampaigns: filteredCampaigns.length,
      activeCampaigns,
      closedCampaigns,
      draftCampaigns,
      totalRegistrations: filteredRegistrations.length,
      paidRegistrations: paidRegs.length,
      pendingRegistrations: pendingRegs.length,
      totalRevenue
    });
  };

  const generateTrendData = () => {
    // Filter registrations based on selection
    const filteredRegistrations = selectedCampaign === 'all'
      ? registrations
      : registrations.filter(r => r.campaignId === selectedCampaign);

    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayRegistrations = filteredRegistrations.filter(r => 
        r.created_at && format(parseISO(r.created_at), 'yyyy-MM-dd') === dateStr
      );
      
      last7Days.push({
        date: format(date, 'MMM dd'),
        registrations: dayRegistrations.length,
        revenue: dayRegistrations
          .filter(r => r.payment_status === 'completed')
          .reduce((sum, r) => sum + (r.total_amount || 0), 0)
      });
    }
    setRegistrationTrend(last7Days);
    setRevenueTrend(last7Days);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Filter registrations based on selection
      const filteredRegistrations = selectedCampaign === 'all'
        ? registrations
        : registrations.filter(r => r.campaignId === selectedCampaign);

      if (filteredRegistrations.length === 0) {
        toast.error('No registrations to export');
        setExporting(false);
        return;
      }

      // Build a per-campaign field_id → label map by fetching form configs
      const distinctCampaignIds = [...new Set(filteredRegistrations.map(r => r.campaignId))];
      const fieldLabelMaps = {};
      await Promise.all(distinctCampaignIds.map(async (cid) => {
        try {
          const res = await api.get(`/campaigns/${cid}/form`);
          const fields = res.data?.fields || [];
          const map = {};
          fields.forEach(f => {
            if (f.id) map[f.id] = f.label || f.id;
            // Name-type fields expose _first / _last sub-keys in form_data
            if (f.type === 'name' && f.id) {
              map[`${f.id}_first`] = `${f.label || 'Name'} (First)`;
              map[`${f.id}_last`]  = `${f.label || 'Name'} (Last)`;
            }
          });
          fieldLabelMaps[cid] = map;
        } catch {
          fieldLabelMaps[cid] = {};
        }
      }));

      const prettifyKey = (k) =>
        k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');

      const resolveLabel = (campaignId, key) => {
        const m = fieldLabelMaps[campaignId] || {};
        return m[key] || prettifyKey(key);
      };

      // Prepare data for Excel
      const data = filteredRegistrations.map(reg => {
        const row = {
          'Event': reg.campaignName,
          'Invoice Number': reg.invoice_number || '',
          'Registration ID': reg.id,
          'Payment Status': reg.payment_status,
          'Total Amount': reg.total_amount,
          'Payment ID': reg.payment_id || '',
          'Created At': format(parseISO(reg.created_at), 'yyyy-MM-dd HH:mm:ss'),
        };

        // Add form data fields — use field labels from form_config
        Object.keys(reg.form_data || {}).forEach(key => {
          const label = resolveLabel(reg.campaignId, key);
          const value = reg.form_data[key];
          // Flatten object values (e.g. file upload objects, name objects) to a readable string
          row[label] = (value && typeof value === 'object')
            ? (value.url || value.name || value.full_name || JSON.stringify(value))
            : value;
        });

        return row;
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Registrations');

      // Add summary sheet
      const summaryData = [
        { Metric: 'Total Registrations', Value: stats.totalRegistrations },
        { Metric: 'Paid Registrations', Value: stats.paidRegistrations },
        { Metric: 'Pending Registrations', Value: stats.pendingRegistrations },
        { Metric: 'Total Revenue', Value: `₹${stats.totalRevenue.toLocaleString()}` },
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Download
      const fileName = selectedCampaign === 'all' 
        ? `all_registrations_${format(new Date(), 'yyyyMMdd')}.xlsx`
        : `registrations_${campaigns.find(c => c.id === selectedCampaign)?.slug || 'campaign'}_${format(new Date(), 'yyyyMMdd')}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      toast.success('Excel exported successfully!');
    } catch (error) {
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <Card className="bg-white rounded-xl border border-slate-100 shadow-card hover:shadow-md transition-shadow duration-200 p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-jakarta text-slate-600 mb-1">{title}</p>
          <h3 className="text-2xl font-outfit font-bold text-slate-900">{value}</h3>
          {subtitle && (
            <p className="text-xs font-jakarta text-slate-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </Card>
  );

  // Get recent registrations for display
  const recentRegistrations = (selectedCampaign === 'all' 
    ? registrations 
    : registrations.filter(r => r.campaignId === selectedCampaign)
  ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // For org admin with single campaign, auto-select it
  const showCampaignFilter = isSuperAdmin() || campaigns.length > 1;
  const selectedCampaignName = selectedCampaign === 'all'
    ? 'All Events'
    : campaigns.find(c => c.id === selectedCampaign)?.name || 'Event';

  return (
    <div className="bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-outfit font-bold text-slate-900">Dashboard</h1>
              <p className="text-sm font-jakarta text-slate-600 mt-1">
                {selectedCampaign === 'all' ? 'Overview of all events' : `Viewing: ${selectedCampaignName}`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Campaign Filter */}
              {showCampaignFilter && (
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <select
                    value={selectedCampaign}
                    onChange={(e) => setSelectedCampaign(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white"
                  >
                    <option value="all">All Events</option>
                    {campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Export Button */}
              <Button
                onClick={handleExportExcel}
                disabled={exporting || registrations.length === 0}
                variant="outline"
                className="border-slate-200 hover:bg-slate-50 font-jakarta font-medium rounded-lg"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Excel'}
              </Button>

              {/* Refresh Button */}
              <Button
                onClick={handleRefresh}
                variant="outline"
                disabled={refreshing}
                className="border-slate-200 hover:bg-slate-50 font-jakarta font-medium rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              {/* Create Campaign (Super Admin only) */}
              {isSuperAdmin() && (
                <Button
                  onClick={() => navigate('/campaigns/create')}
                  data-testid="create-campaign-button"
                  className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg shadow-lg shadow-primary/20"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {/* Campaign Stats - Only show for Super Admin or when viewing all */}
          {(isSuperAdmin() || selectedCampaign === 'all') && (
            <>
              <StatCard
                icon={CheckCircle}
                title="Active"
                value={stats.activeCampaigns}
                subtitle="events"
                color="bg-green-500"
              />
              <StatCard
                icon={XCircle}
                title="Inactive"
                value={stats.inactiveCampaigns}
                subtitle="events"
                color="bg-slate-400"
              />
            </>
          )}
          
          <StatCard
            icon={Users}
            title="Registrations"
            value={stats.totalRegistrations}
            subtitle={`${stats.paidRegistrations} paid`}
            color="bg-primary"
          />
          <StatCard
            icon={TrendingUp}
            title="Paid"
            value={stats.paidRegistrations}
            color="bg-green-500"
          />
          <StatCard
            icon={Calendar}
            title="Pending"
            value={stats.pendingRegistrations}
            color="bg-yellow-500"
          />
          <StatCard
            icon={DollarSign}
            title="Revenue"
            value={`₹${(stats.totalRevenue * 0.9705).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            color="bg-secondary"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Registration Trend */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-outfit font-semibold text-slate-900">Registration Trend</h2>
              <span className="text-xs font-jakarta text-slate-500">Last 7 days</span>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={registrationTrend}>
                  <defs>
                    <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="registrations" 
                    stroke="#7c3aed" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorRegistrations)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Revenue Trend */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-outfit font-semibold text-slate-900">Revenue Trend</h2>
              <span className="text-xs font-jakarta text-slate-500">Last 7 days</span>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip 
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Bar dataKey="revenue" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Recent Registrations & Campaigns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Registrations */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-outfit font-semibold text-slate-900">Recent Registrations</h2>
              <Activity className="w-4 h-4 text-slate-400" />
            </div>

            {recentRegistrations.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-jakarta text-sm">No registrations yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentRegistrations.map((reg) => (
                  <div
                    key={reg.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-jakarta font-medium text-slate-900 text-sm">
                          {getRegistrantName(reg.form_data)}
                        </p>
                        <p className="text-xs text-slate-500">{reg.campaignName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-outfit font-semibold text-slate-900 text-sm">
                        ₹{(reg.total_amount || 0).toFixed(0)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        reg.payment_status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {reg.payment_status === 'completed' ? 'Paid' : 'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Campaigns List */}
          <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-outfit font-semibold text-slate-900">
                {isSuperAdmin() ? 'All Events' : 'My Events'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/campaigns')}
                className="hover:bg-slate-100 text-slate-600 text-xs"
              >
                View All
              </Button>
            </div>

            {campaigns.length === 0 ? (
              <div className="text-center py-6">
                <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 font-jakarta text-sm">No events</p>
              </div>
            ) : (
              <div className="space-y-2">
                {campaigns.slice(0, 5).map((campaign) => (
                  <div
                    key={campaign.id}
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-jakarta font-medium text-slate-900 text-sm truncate">
                        {campaign.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {campaign.current_registrations} registrations
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                      campaign.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {campaign.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
